import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Markdown from "@/components/Markdown";
import { HEALTH_COLORS, HEALTH_LABELS } from "@/lib/constants";
import { Loader2, Presentation, Copy, RefreshCw } from "lucide-react";

export default function ExecSummaryDialog({ open, onOpenChange, projectId }) {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [proj, setProj] = useState(null);

  useEffect(() => {
    if (open) api.get(`/projects/${projectId}`).then(({ data }) => setProj(data)).catch(() => {});
  }, [open, projectId]);

  const generate = async () => {
    setLoading(true);
    setSummary("");
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/executive-summary`);
      setSummary(data.summary);
      setMetrics(data.metrics);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to generate");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && !summary && !loading) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="exec-summary-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Presentation className="h-5 w-5 text-primary" /> Executive summary</DialogTitle>
        </DialogHeader>
        {proj?.description && (
          <p className="text-sm text-muted-foreground -mt-1 border-l-2 border-primary/40 pl-3" data-testid="exec-project-description">{proj.description}</p>
        )}

        {metrics && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="exec-metrics">
            <div className="border border-border rounded-md p-2 text-center">
              <p className="text-[10px] text-muted-foreground uppercase">Progress</p>
              <p className="font-display text-xl font-bold">{metrics.avg_percent}%</p>
            </div>
            {["blue", "green", "yellow", "red", "gray"].filter((k) => metrics.health?.[k]).slice(0, 4).map((k) => (
              <div key={k} className="border border-border rounded-md p-2 text-center">
                <p className="text-[10px] text-muted-foreground uppercase truncate">{HEALTH_LABELS[k]}</p>
                <p className="font-display text-xl font-bold" style={{ color: HEALTH_COLORS[k] }}>{metrics.health[k]}</p>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Composing the narrative...</div>
        ) : summary ? (
          <div>
            <div className="flex justify-end gap-3 mb-2">
              <button onClick={() => { navigator.clipboard.writeText(summary); toast.success("Copied"); }} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="exec-copy-button"><Copy className="h-3 w-3" /> Copy</button>
              <button onClick={generate} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="exec-regenerate-button"><RefreshCw className="h-3 w-3" /> Regenerate</button>
            </div>
            <Markdown text={summary} />
          </div>
        ) : (
          <div className="text-center py-10">
            <Button onClick={generate} className="gap-2" data-testid="exec-generate-button"><Presentation className="h-4 w-4" /> Generate summary</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
